'use client';
import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import LinkCustom from '@/app/_components/link/LinkCustom';
import { PageDescription } from '@/app/_components/typography/page-description/page-description';
import { PageTitle } from '@/app/_components/typography/page-title/page-title';
import { ReactComponent as ArrowForwardCircle } from '@/app/_icons/ArrowForwardCircleDark.svg';
import { InstagramFeedResponse, MediaTypeEnum } from '@/types/blog/blog.types';
import { OpeningParagraph } from '@/types/opening-paragraph/opening-paragraph.types';

import NewsCard from '../NewsCard';

import Styles from './BlogList.module.scss';

type SocialMediaImageComponentProps = {
  src: string;
  className: string | undefined;
  width?: string;
  height?: string;
  objectFit?: string | undefined;
  alt?: string;
  type?: string;
};

const SocialMediaImageComponent = ({
  src,
  type,
  className,
  width = '400',
  height = '369',
  objectFit = 'cover',
  alt = '',
}: SocialMediaImageComponentProps) => {
  if (type === MediaTypeEnum.VIDEO) {
    return (
      <video playsInline autoPlay loop muted className={Styles.socialMediaImg}>
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    );
  } else {
    return (
      <Image
        src={src}
        className={className}
        width={+width}
        height={+height}
        objectFit={objectFit}
        alt={alt}
      />
    );
  }
};

type BlogListProps = {
  date_to_publish: string;
  mainImage: string | null;
  headerTitle: string;
  slug: string;
}[];

type Props = {
  instagramData: InstagramFeedResponse;
  latestNewsBlogs: BlogListProps;
  openingParagraph: OpeningParagraph;
};

const BlogList = ({
  instagramData,
  latestNewsBlogs,
  openingParagraph,
}: Props) => {
  const [instagramFeed, setInstagramFeed] = useState(instagramData?.data || []);
  const [nextUrl, setNextUrl] = useState(instagramData?.paging?.next || null);
  const [error, setError] = useState('');

  const handleNextClick = async () => {
    if (nextUrl) {
      try {
        const response = await fetch(nextUrl);

        if (response.ok) {
          const newFeed: InstagramFeedResponse = await response.json();

          if (newFeed.data) {
            setInstagramFeed((prevFeed) => [...prevFeed, ...newFeed.data]);
            setNextUrl(newFeed.paging?.next || null);
          } else {
            setError('No more data available.');
          }
        } else {
          throw new Error('Failed to fetch data');
        }
      } catch (err) {
        setError((err as Error).message);
      }
    } else {
      setError('No more data available.');
    }
  };

  return (
    <>
      <Box className={Styles.blogListWrapper} paddingY={{ base: 14, sm: 20 }}>
        <Container maxW={1280} width={'100%'} paddingX={{ base: 6, '2xl': 0 }}>
          <Box
            marginBottom={{ base: 16, md: 10 }}
            className={Styles.blogListHeader}
          >
            {openingParagraph?.title && (
              <PageTitle>{openingParagraph?.title}</PageTitle>
            )}
            {openingParagraph?.description && (
              <PageDescription>{openingParagraph?.description}</PageDescription>
            )}
          </Box>
          {instagramFeed.length > 0 ? (
            <SimpleGrid columns={[1, 1, 2, 3]} gap={10} mb={12}>
              {instagramFeed.map((feed) => {
                const { id, media_url, media_type, permalink } = feed;

                return (
                  <Link
                    href={permalink}
                    role="link"
                    key={id}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SocialMediaImageComponent
                      type={media_type}
                      src={media_url}
                      className={Styles.socialMediaImg}
                      width="400"
                      height="369"
                      objectFit="cover"
                      alt={'News Feed Image'}
                    />
                  </Link>
                );
              })}
            </SimpleGrid>
          ) : (
            <Text>No data available.</Text>
          )}
          {error && <Text color="red.500">{error}</Text>}
          <Box display={'flex'} alignItems={'center'} justifyContent={'end'}>
            <Box className={Styles.blogListPagination}>
              <span>View more</span>
            </Box>
            <Box className={Styles.blogListNavigation}>
              <button
                type="button"
                role="button"
                aria-label="Next"
                className={Styles.blogListNext}
                onClick={handleNextClick}
              >
                <ArrowForwardCircle />
              </button>
            </Box>
          </Box>
        </Container>
      </Box>
      <Box
        className={Styles.latestNewsWrapper}
        paddingY={{ base: 20, sm: '100px' }}
      >
        <Container maxW={1280} width={'100%'} paddingX={{ base: 6, '2xl': 0 }}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            flexWrap={'wrap'}
            alignItems={{ base: 'start', md: 'center' }}
            marginBottom={{ base: 16, md: 10 }}
            className={Styles.latestNewsHeader}
          >
            <Box className={Styles.headingElement}>
              <Text
                fontSize="xs"
                textTransform={'uppercase'}
                color={'white'}
                marginBottom={4}
              >
                Must read recommended articles
              </Text>
              <Heading
                as="h2"
                textTransform={'capitalize'}
                className={Styles.titleLg}
                color={'white'}
              >
                Latest News
              </Heading>
            </Box>
            <Box
              mt={{ base: 8, md: 0 }}
              marginLeft={{ md: 'auto' }}
              className={Styles.latestNewsHeaderCTA}
            >
              <LinkCustom
                role="link"
                aria-label="View All"
                className="btnOutlineWhite"
                href="/blog/latest-news"
              >
                View all
                <ChevronRightIcon width={4} height={4} marginLeft={3} />
              </LinkCustom>
            </Box>
          </Flex>
          <SimpleGrid columns={[1, 1, 2, 3]} gap={10}>
            {latestNewsBlogs?.map((blog, i) => (
              <NewsCard
                key={i}
                image={blog?.mainImage}
                title={blog?.headerTitle}
                date_to_publish={blog?.date_to_publish}
                slug={blog?.slug ?? ''}
              />
            ))}
          </SimpleGrid>
        </Container>
      </Box>
    </>
  );
};

export default BlogList;
